import React, { useState } from 'react'
import { isEmpty } from 'lodash'

import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import {
  INSIGHT_KIND,
  INSIGHT_KIND_LABEL,
  INSIGHT_KIND_ORDER,
  SCOPE_ALL,
} from './AgenticInsights.constants'
import {
  getActionableInsights,
  getEmptyStateKind,
  getExecutiveInsights,
  getLaunchedInsights,
  getScopeEntries,
  groupInsightsByScope,
} from './AgenticInsights.helpers'
import { InsightSpineCard } from './InsightSpineCard'
import { HeroInsightCard } from './HeroInsightCard'
import { ExecutiveInsightCard } from './ExecutiveInsightCard'
import { EmptyState } from './EmptyState'

// Insights = Act. A sub-toggle (upper right) splits the two audiences: Agent
// insights (on-call, launchable) default; Executive insights (off-call
// process/training fixes) on demand. Under "All playbooks" either list is
// grouped by run.
export const InsightsTab = ({
  scopeInsights,
  scope,
  playbooks,
  hasExpiredInsights,
  hasDismissedInsights,
  onOpen,
  onDismiss,
}) => {
  const [kind, setKind] = useState(INSIGHT_KIND.AGENT)

  const renderCards = (list, CardComponent) => {
    if (scope === SCOPE_ALL) {
      const groups = groupInsightsByScope(list, playbooks)
      return (
        <div className="agentic-groups">
          {Object.entries(groups).map(([playbookName, groupInsights]) => (
            <section key={playbookName} className="agentic-group">
              <h3 className="agentic-group__head">{playbookName}</h3>
              {groupInsights.map((insight) => (
                <CardComponent
                  key={insight.id}
                  insight={insight}
                  onOpen={onOpen}
                  onDismiss={onDismiss}
                />
              ))}
            </section>
          ))}
        </div>
      )
    }
    return (
      <div className="agentic-stack">
        {list.map((insight) => (
          <CardComponent key={insight.id} insight={insight} onOpen={onOpen} onDismiss={onDismiss} />
        ))}
      </div>
    )
  }

  const renderAgent = () => {
    const actionable = getActionableInsights(scopeInsights)
    if (isEmpty(actionable)) {
      if (scope === SCOPE_ALL && !hasExpiredInsights) {
        return (
          <p className="agentic-muted">No launch-ready insights across your playbooks right now.</p>
        )
      }
      const emptyKind = getEmptyStateKind({
        scopeInsights,
        actionableInsights: actionable,
        launchedInsights: getLaunchedInsights(scopeInsights),
        hasExpiredInsights,
        hasDismissedInsights,
      })
      return <EmptyState kind={emptyKind} />
    }
    // getActionableInsights is ranked by expected weekly conversions — an absolute,
    // cross-run-comparable impact number — so the first is the highest-impact
    // recommendation across whatever's in scope. Spotlight it in the hero and list
    // the rest below (renderCards drops it since we pass `rest`, not the full list).
    // Under "All playbooks" tag the hero with its run so it isn't detached from
    // context; within a single run that label is redundant.
    const [hero, ...rest] = actionable
    const heroScopeLabel = scope === SCOPE_ALL ? getScopeEntries(hero, playbooks)[0]?.name : null
    return (
      <>
        <HeroInsightCard
          insight={hero}
          onOpen={onOpen}
          onDismiss={onDismiss}
          scopeLabel={heroScopeLabel}
        />
        {!isEmpty(rest) && renderCards(rest, InsightSpineCard)}
      </>
    )
  }

  const renderExecutive = () => {
    const executive = getExecutiveInsights(scopeInsights)
    if (isEmpty(executive)) {
      return (
        <p className="agentic-muted">
          No executive insights for this run — nothing off-call flagged for your team.
        </p>
      )
    }
    return renderCards(executive, ExecutiveInsightCard)
  }

  return (
    <>
      <div className="agentic-subtabs">
        <SegmentedControl
          items={INSIGHT_KIND_ORDER.map((kindKey) => ({
            accessor: kindKey,
            active: kind === kindKey,
            onClick: () => setKind(kindKey),
            label: INSIGHT_KIND_LABEL[kindKey],
            dataTestId: `agentic-insight-kind-${kindKey}`,
          }))}
        />
      </div>
      {kind === INSIGHT_KIND.AGENT ? renderAgent() : renderExecutive()}
    </>
  )
}
